/**
 * 屏 13 — 我的作品集
 *
 * 功能：
 *   - 展示"我的作品"（编辑器创建的项目）
 *   - 展示"铸剑阁作品"（完成关卡后的成就）
 *   - 筛选：来源（全部/我的作品/铸剑阁作品）+ 类型（1v1/2v2/3v3）
 *   - 排序：最新优先
 *   - 卡片操作：编辑/删除/使用/重玩/分享
 *
 * 设计约束（Ardot v2.0）：
 *   - 卡片背景 #0D1117, border 2px #2A2A3A
 *   - hover/选中时 border → --tactical-cyan
 *   - 类型标签用 --valorant-red / --tactical-green / --tactical-amber
 *   - 无圆角、无阴影、无渐变
 *   - monospace 字体
 */

import { pathFor } from "../router/screen-router";
import { getAggregatedWorks, searchWorks, type FilterOptions } from "../services/works-aggregator";
import { deleteMyProject, type Work, type WorkSource, type BattleType } from "../storage/works-store";
import { TOKEN_MAP } from "../utils/color-tokens";
import { renderHeaderHTML, bindHeaderEvents } from "../components/header-bar";
import { getForgeLevelDifficulty } from "../utils/forge-difficulty";

// ============================================================
// 状态
// ============================================================

let currentFilter: FilterOptions = { source: "all", type: "all" };
let searchQuery = "";

// ============================================================
// 工具函数
// ============================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

/** 格式化时间戳 */
function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} 天前`;
  if (hours > 0) return `${hours} 小时前`;
  if (minutes > 0) return `${minutes} 分钟前`;
  return "刚刚";
}

/** 获取类型标签颜色 */
function getTypeColor(type: BattleType): string {
  switch (type) {
    case "1v1":
      return TOKEN_MAP.green();
    case "2v2":
      return TOKEN_MAP.amber();
    case "3v3":
      return TOKEN_MAP.red();
    default:
      return TOKEN_MAP.textMuted();
  }
}

// 注：getSourceLabel 函数保留以备后续使用（如卡片来源标签）

// ============================================================
// 渲染
// ============================================================

function renderFilterBar(): string {
  const sources: Array<{ value: WorkSource | "all"; label: string }> = [
    { value: "all", label: "全部" },
    { value: "editor", label: "我的作品" },
    { value: "forge", label: "铸剑阁作品" },
  ];

  const types: Array<{ value: BattleType | "all"; label: string }> = [
    { value: "all", label: "全部类型" },
    { value: "1v1", label: "1v1" },
    { value: "2v2", label: "2v2" },
    { value: "3v3", label: "3v3" },
  ];

  const sourceOptions = sources
    .map(
      (s) =>
        `<option value="${s.value}" ${currentFilter.source === s.value ? "selected" : ""}>${s.label}</option>`
    )
    .join("");

  const typeOptions = types
    .map(
      (t) =>
        `<option value="${t.value}" ${currentFilter.type === t.value ? "selected" : ""} ${currentFilter.source === "forge" ? "disabled" : ""}>${t.label}</option>`
    )
    .join("");

  return `
    <div class="s13-filter-bar">
      <div class="s13-filter-group">
        <label>来源</label>
        <select id="s13-source-filter" class="s13-filter-select">
          ${sourceOptions}
        </select>
      </div>
      <div class="s13-filter-group">
        <label>类型</label>
        <select id="s13-type-filter" class="s13-filter-select" ${currentFilter.source === "forge" ? "disabled" : ""}>
          ${typeOptions}
        </select>
      </div>
      <div class="s13-search-group">
        <label>搜索</label>
        <input
          type="text"
          id="s13-search-input"
          class="s13-search-input"
          placeholder="输入作品名称..."
          value="${escapeHtml(searchQuery)}"
        />
      </div>
    </div>
  `;
}

/** 评分等级颜色映射（Ardot 战术美学 token） */
function scoreToColor(score: string): string {
  const colors: Record<string, string> = {
    S: getComputedStyle(document.documentElement).getPropertyValue("--tactical-amber") || "#F8B700",
    A: getComputedStyle(document.documentElement).getPropertyValue("--tactical-green") || "#7FD962",
    B: getComputedStyle(document.documentElement).getPropertyValue("--tactical-cyan") || "#00E4FF",
    C: getComputedStyle(document.documentElement).getPropertyValue("--valorant-red") || "#FF4655",
  };
  return colors[score] || "var(--arsenic, #8B95A5)";
}

/** 难度星级渲染 ★★★☆☆ */
function renderDifficultyStars(difficulty: number): string {
  const max = 5;
  const filled = Math.min(Math.max(difficulty || 1, 1), max);
  const stars = "★".repeat(filled) + "☆".repeat(max - filled);
  return `<span class="s13-difficulty" title="难度 ${filled}/5">${stars}</span>`;
}

function renderWorkCard(work: Work, aggregated: any): string {
  const isMyProject = work.source === "editor";
  const isForge = work.source === "forge";
  const typeLabel = isMyProject ? (work as any).type : "";
  const typeColor = isMyProject ? getTypeColor((work as any).type) : "";

  // 铸剑阁评分 + 难度
  let forgeBadges = "";
  if (isForge) {
    const score = aggregated.score;
    const difficulty = aggregated.difficulty || getForgeLevelDifficulty((work as any).chapterNum, (work as any).levelId);
    const scoreColor = score ? scoreToColor(score) : "";
    forgeBadges = `
      <div class="s13-forge-badges">
        ${score ? `<span class="s13-score-badge" style="border-color: ${scoreColor}; color: ${scoreColor};">${escapeHtml(score)}</span>` : ""}
        <span class="s13-difficulty-stars">${renderDifficultyStars(difficulty)}</span>
      </div>`;
  }

  const actionButtons = isMyProject
    ? `
      <button class="s13-card-btn s13-btn-edit" data-id="${work.id}" data-action="edit">编辑</button>
      <button class="s13-card-btn s13-btn-use" data-id="${work.id}" data-action="use">使用</button>
      <button class="s13-card-btn s13-btn-delete" data-id="${work.id}" data-action="delete">删除</button>
    `
    : `
      <button class="s13-card-btn s13-btn-replay" data-id="${work.id}" data-action="replay">重玩</button>
    `;

  return `
    <div class="s13-work-card" data-id="${work.id}" data-source="${work.source}">
      <div class="s13-card-header">
        <div class="s13-card-title">${escapeHtml(aggregated.title)}</div>
        ${typeLabel ? `<span class="s13-type-badge" style="color: ${typeColor}">${typeLabel}</span>` : ""}
      </div>
      <div class="s13-card-body">
        <div class="s13-card-description">${escapeHtml(aggregated.description)}</div>
        ${forgeBadges}
        ${aggregated.tags.length > 0 ? `<div class="s13-card-tags">${aggregated.tags.map((tag: string) => `<span class="s13-tag">${escapeHtml(tag)}</span>`).join("")}</div>` : ""}
      </div>
      <div class="s13-card-footer">
        <div class="s13-card-time">${formatTime(aggregated.timestamp)}</div>
        <div class="s13-card-actions">
          ${actionButtons}
        </div>
      </div>
    </div>
  `;
}

function renderWorksList(works: any[]): string {
  if (works.length === 0) {
    return `
      <div class="s13-empty-state">
        <img class="s13-empty-img" src="/images/ui/empty-works.png" alt="暂无作品" loading="lazy">
        <div class="s13-empty-text">暂无作品</div>
      </div>
    `;
  }

  return works.map((w) => renderWorkCard(w.work, w)).join("");
}

// ============================================================
// 事件处理
// ============================================================

function bindFilterEvents(container: HTMLElement): void {
  const sourceSelect = container.querySelector("#s13-source-filter") as HTMLSelectElement;
  const typeSelect = container.querySelector("#s13-type-filter") as HTMLSelectElement;
  const searchInput = container.querySelector("#s13-search-input") as HTMLInputElement;

  if (sourceSelect) {
    sourceSelect.addEventListener("change", (e) => {
      currentFilter.source = (e.target as HTMLSelectElement).value as WorkSource | "all";
      // 如果切换到铸剑阁，禁用类型筛选
      if (currentFilter.source === "forge") {
        currentFilter.type = "all";
      }
      refreshWorks(container);
    });
  }

  if (typeSelect) {
    typeSelect.addEventListener("change", (e) => {
      currentFilter.type = (e.target as HTMLSelectElement).value as BattleType | "all";
      refreshWorks(container);
    });
  }

  if (searchInput) {
    searchInput.addEventListener("input", (e) => {
      searchQuery = (e.target as HTMLInputElement).value;
      refreshWorks(container);
    });
  }
}

function bindCardEvents(container: HTMLElement): void {
  container.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const btn = target.closest(".s13-card-btn") as HTMLElement;
    if (!btn) return;

    const id = btn.getAttribute("data-id");
    const action = btn.getAttribute("data-action");
    if (!id || !action) return;

    handleCardAction(id, action, container);
  });
}

function handleCardAction(id: string, action: string, container: HTMLElement): void {
  switch (action) {
    case "edit":
      // 跳转到编辑器
      window.location.hash = pathFor("editor", id);
      break;

    case "use":
      // 跳转到砺锋台（选手选择器）
      // 这里需要保存选中的项目 ID，砺锋台会读取
      sessionStorage.setItem("battlecode-selected-project", id);
      window.location.hash = pathFor("sharpen", null);
      break;

    case "delete":
      if (confirm("确定删除这个作品吗？")) {
        deleteMyProject(id);
        refreshWorks(container);
      }
      break;

    case "replay":
      // V9: 重玩铸剑阁关卡
      const work = getAggregatedWorks().find((w) => w.work.id === id);
      if (work && work.work.source === "forge") {
        const forgeWork = work.work as any;
        // 设置重玩标记，屏 12 会自动选中该关卡
        sessionStorage.setItem("battlecode-forge-replay", forgeWork.levelId);
        // 导航到屏 12 铸剑阁
        window.location.hash = pathFor("forge", null);
      }
      break;
  }
}

function refreshWorks(container: HTMLElement): void {
  const works = searchQuery
    ? searchWorks(searchQuery, currentFilter)
    : getAggregatedWorks(currentFilter);

  const worksList = container.querySelector(".s13-works-list") as HTMLElement | null;
  if (worksList) {
    worksList.innerHTML = renderWorksList(works);
    bindCardEvents(worksList);
  }
}

// ============================================================
// 主渲染函数
// ============================================================

export function renderScreen13MyWorks(container: HTMLElement, go: (path: string) => void): void {
  const works = searchQuery ? searchWorks(searchQuery, currentFilter) : getAggregatedWorks(currentFilter);

  const headerHTML = renderHeaderHTML({
    backPath: pathFor("armory-hall"),
    title: "演 兵 室",
    subtitle: "ARSENAL · PARADE"
  });

  container.innerHTML = `
    <div class="s13-container">
      ${headerHTML}

      ${renderFilterBar()}

      <div class="s13-works-list">
        ${renderWorksList(works)}
      </div>
    </div>
  `;

  // 绑定事件
  bindHeaderEvents(go, pathFor("armory-hall"));
  bindFilterEvents(container);
  bindCardEvents(container.querySelector(".s13-works-list")!);

  // 添加卡片进入动画
  setTimeout(() => {
    const cards = container.querySelectorAll(".s13-work-card");
    cards.forEach((card, index) => {
      (card as HTMLElement).style.animation = `fadeInUp 0.4s ease-out ${index * 0.05}s both`;
    });
  }, 0);
}

/** 清理屏 13 */
export function cleanupScreen13(): void {
  // 重置状态
  currentFilter = { source: "all", type: "all" };
  searchQuery = "";
}
